// Retken tallennus ja lähetysjono.
//
// Puhelin tallentaa ensin, lähettää sitten. Jokainen GPS-piste kirjoitetaan
// heti pysyvään tallennukseen, joten sovelluksen kaatuminen tai yhteyskatko
// ei hävitä jo kuljettua matkaa (suunnitelman kohta 23). Lähetys onnistuu
// myöhemmin, eikä uudelleenlähetys tuplaa mitään, koska palvelin tunnistaa
// retken UUID:stä ja pisteet juoksevasta numerosta (seq).
//
// Tila:
//   active  – käynnissä oleva retki tai null
//   outbox  – päättyneet retket, joiden lähetys on vielä kesken
//   discards – hylätyt retket, joiden poisto palvelimelta on vielä kesken (ks. discard)
//
// Pistevarasto (journal, valinnainen): jokainen piste kirjoitetaan myös omana
// IndexedDB-kirjauksenaan (pointJournal.js) ja poistetaan sieltä vasta, kun palvelin
// on kuitannut sen. Kun piste on varmasti varastossa, sitä ei enää kirjoiteta
// uudelleen koko tilan mukana (localStorage), joten tilakirjoitus pysyy pienenä.
// Ilman varastoa (vanha WebView, yksityinen tila) Recorder toimii kuten ennen.
//
// Recorder ei tiedä mitään käyttöliittymästä. Se kertoo muutoksista
// onChange-kutsulla ja palvelimen myöntämistä palkinnoista onAwards-kutsulla.

import { distanceM, tileKey } from "./geo.js";

const STORAGE_KEY = "recorder";
const BATCH_SIZE = 500;
// Kartalla näkyvä jälki pidetään pienenä, koska se kirjoitetaan koko tilan mukana
// localStorageen joka pisteellä (suunnitelman kohta 4.2). Täydet pisteet ovat pistevarastossa.
export const TRACK_MIN_STEP_M = 3; // tätä lähempänä edellistä jäljen pistettä ei lisätä
export const TRACK_MAX_POINTS = 2000; // yli tämän jäljestä poistetaan joka toinen piste

/** Lisää pisteen näytettävään jälkeen harvennettuna ja rajaa jäljen pituuden. */
export function addTrackPoint(track, lat, lon) {
  const last = track[track.length - 1];
  if (last && distanceM(last[0], last[1], lat, lon) < TRACK_MIN_STEP_M) return;
  track.push([lat, lon]);
  if (track.length > TRACK_MAX_POINTS) {
    // Ensimmäinen ja viimeinen säilyvät; muista jää joka toinen.
    const kept = track.filter((_, i) => i === 0 || i === track.length - 1 || i % 2 === 0);
    track.splice(0, track.length, ...kept);
  }
}
// Samat rajat kuin palvelimella (backend/app/config.py), jotta retken aikana
// näkyvä matka vastaa palvelimen laskemaa.
const MAX_ACCURACY_M = 50;
const GAP_SECONDS = 120;
const GAP_DISTANCE_M = 300;

export class Recorder {
  /**
   * journal: pistevarasto (pointJournal.js) tai null. journalPoints: varaston pisteet
   * käynnistyshetkellä (Map tripId -> pisteet), luettu ennen Recorderin rakentamista,
   * koska konstruktori on synkroninen.
   */
  constructor({ api, storage, journal = null, journalPoints = new Map(), now = () => Date.now() / 1000, uuid = () => crypto.randomUUID(), smallZoom = 17, onChange = () => {}, onAwards = () => {}, onCompanions = () => {} }) {
    this.api = api;
    this.storage = storage;
    this.now = now;
    this.uuid = uuid;
    this.smallZoom = smallZoom;
    this.onChange = onChange;
    this.onAwards = onAwards;
    this.onCompanions = onCompanions;
    this.syncing = null;
    this.lastError = null;
    this.lastStats = null;
    this.journal = journal;
    // Pisteet, jotka tiedetään olevan pysyvästi varastossa. WeakSet, jotta pistettä ei
    // tarvitse merkitä (data pysyy samana kuin palvelimelle lähtevä) eikä sivuvaikutuksia synny.
    this.journaled = new WeakSet();
    this.pendingWrites = new Set();
    this.orphanPoints = 0; // varaston pisteet, joiden retkeä ei löydy tilasta (ei poisteta)

    const saved = storage.get(STORAGE_KEY, null);
    this.active = saved?.active ?? null;
    this.outbox = saved?.outbox ?? [];
    this.discards = saved?.discards ?? [];
    if (journal) this._restoreFromJournal(journalPoints);
  }

  /**
   * Yhdistää tilan ja varaston pisteet. Piste voi olla kummassa tahansa tai molemmissa:
   *  - vain varastossa: normaali tapaus (tila kirjoitettiin ilman varmistettua pistettä)
   *  - vain tilassa: vanha tallennus tai varastokirjoitus ei ehtinyt valmistua
   *  - molemmissa: päällekkäisyys poistetaan seq-numerolla
   * Tilan vanhemmaksi jäänyt laskuri (nextSeq, lastT) nostetaan varaston mukaan, jotta
   * uudet pisteet eivät saa jo käytettyä numeroa.
   */
  _restoreFromJournal(journalPoints) {
    const known = new Set();
    for (const trip of [this.active, ...this.outbox].filter(Boolean)) {
      known.add(trip.id);
      const stored = journalPoints.get(trip.id) ?? [];
      const storedSeqs = new Set(stored.map((p) => p.seq));
      const merged = new Map();
      for (const p of trip.unsent ?? []) merged.set(p.seq, p);
      for (const p of stored) { merged.set(p.seq, p); this.journaled.add(p); }
      trip.unsent = [...merged.values()].sort((a, b) => a.seq - b.seq);
      if (trip.unsent.length) {
        const last = trip.unsent[trip.unsent.length - 1];
        trip.nextSeq = Math.max(trip.nextSeq ?? 0, last.seq + 1);
        trip.lastT = Math.max(trip.lastT ?? 0, last.t);
      }
      // Vanhan tallennuksen pisteet siirretään varastoon (migraatio). Tila säilyttää ne,
      // kunnes siirto on vahvistettu, joten mikään piste ei ole hetkeäkään vain muistissa.
      const legacy = trip.unsent.filter((p) => !storedSeqs.has(p.seq));
      if (legacy.length) this._track(this.journal.appendMany(trip.id, legacy).then(() => legacy.forEach((p) => this.journaled.add(p))));
    }
    for (const [id, points] of journalPoints) if (!known.has(id)) this.orphanPoints += points.length;
  }

  /** Seuraa varastokirjoitusta: virhe ei kaada tallennusta, piste jää tilaan. */
  _track(promise) {
    const tracked = promise.catch((error) => { this.lastJournalError = String(error?.message ?? error); });
    this.pendingWrites.add(tracked);
    tracked.finally(() => this.pendingWrites.delete(tracked));
    return tracked;
  }

  /** Odottaa kaikkia keskeneräisiä varastokirjoituksia (testit ja retken päättäminen). */
  async journalIdle() {
    while (this.pendingWrites.size) await Promise.all([...this.pendingWrites]);
  }

  // --- Tila ulos -------------------------------------------------------------

  get pendingPointCount() {
    return [this.active, ...this.outbox].filter(Boolean).reduce((sum, trip) => sum + trip.unsent.length, 0);
  }

  snapshot() {
    return {
      active: this.active && {
        id: this.active.id,
        player: this.active.player ?? null,
        companions: this.active.companions ?? [],
        mode: this.active.mode,
        startedAt: this.active.startedAt,
        distanceM: this.active.distanceM,
        pointCount: this.active.nextSeq,
        localTiles: this.active.localTiles,
        track: this.active.track,
        newTiles: this.active.newTiles,
        earned: this.active.earned,
        pausedAt: this.active.pausedAt ?? null,
        pausedTotal: this.active.pausedTotal ?? 0,
      },
      outboxCount: this.outbox.length,
      discardsPending: this.discards.length,
      pendingPoints: this.pendingPointCount,
      lastError: this.lastError,
      storageFailed: Boolean(this.storageFailed),
      syncing: Boolean(this.syncing),
    };
  }

  persist() {
    const savedAt = Date.now();
    // Varastossa olevat pisteet jätetään pois: ne ovat jo pysyvästi tallessa. Jos varasto
    // ei toimi, yksikään piste ei ole "varmistettu", ja tila sisältää ne kuten ennen.
    const slim = (trip) => (trip && this.journal ? { ...trip, unsent: trip.unsent.filter((p) => !this.journaled.has(p)) } : trip);
    const ok = this.storage.set(STORAGE_KEY, { active: slim(this.active), outbox: this.outbox.map(slim), discards: this.discards, savedAt });
    this.storageFailed = !ok; // näkyy retkipaneelissa (lib/recStatus.js), ei vain virheviestinä
    if (!ok) this.lastError = "Puhelimen tallennustila on täynnä. Lähetä retki palvelimelle mahdollisimman pian.";
    this.onChange(this.snapshot());
  }

  // --- Retken elinkaari ------------------------------------------------------

  /**
   * player: pelaaja, jonka tietoihin retki tallentuu. companions: retkellä
   * mukana olevat muut pelaajat; palvelin kopioi retken heille retken päättyessä.
   * Molemmat tallennetaan retken mukana, jotta jonosta lähetys menee oikealle
   * pelaajalle myös silloin, kun käyttäjä on sillä välin vaihtanut pelaajaa.
   */
  start(mode, { player = null, companions = [] } = {}) {
    if (this.active) throw new Error("Retki on jo käynnissä.");
    const startedAt = this.now();
    this.active = {
      id: this.uuid(),
      player,
      companions,
      startedAt,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Helsinki",
      initialMode: mode,
      mode,
      created: false,
      modeQueue: [], // retken aikana tehdyt vaihdot, joita palvelin ei vielä tiedä
      pauseQueue: [], // tauot {start_t, end_t}, joita palvelin ei vielä tiedä
      pausedAt: null, // käynnissä olevan tauon alku tai null
      pausedTotal: 0, // päättyneiden taukojen yhteiskesto sekunteina
      unsent: [], // lähettämättömät pisteet
      nextSeq: 0,
      lastT: 0,
      lastGood: null, // edellinen tarkka piste matkan laskentaan
      distanceM: 0,
      track: [], // kevennetty jälki kartalle [[lat, lon], ...]
      localTiles: [], // paikallisesti lasketut ruudut, odottavat vahvistusta
      newTiles: 0, // palvelimen vahvistamat uudet pikkuruudut tällä retkellä
      earned: 0, // palvelimen vahvistamat pisteet tällä retkellä
      finishedAt: null,
    };
    this.persist();
    return this.active.id;
  }

  /**
   * Uusi sijainti laitteelta. time on millisekunteja (kuten selaimen ja
   * Capacitorin sijaintirajapinnoissa).
   */
  addLocation({ latitude, longitude, accuracy = null, time }) {
    const trip = this.active;
    if (!trip || trip.pausedAt) return; // tauon aikana sijaintia ei tallenneta
    // Hylkää mahdottomat koordinaatit ennen jonoon tallennusta ja
    // ruutulaskentaa; GPS-plugin voi palauttaa puutteellisen näytteen.
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)
      || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
      this.lastError = "GPS palautti virheelliset koordinaatit; piste ohitettiin.";
      this.onChange(this.snapshot());
      return;
    }
    const t = (time ?? Date.now()) / 1000;
    if (!Number.isFinite(t) || t <= trip.lastT) return; // sama tai vanhempi sijainti: ohitetaan

    trip.lastT = t;
    const point = { seq: trip.nextSeq++, t, lat: latitude, lon: longitude, accuracy };
    trip.unsent.push(point);
    // Oma kirjoitus heti. Kunnes se on valmis, piste on mukana tilakirjoituksessa
    // (persist alla), joten kumpikin tie kattaa toisen.
    if (this.journal) this._track(this.journal.append(trip.id, point).then(() => this.journaled.add(point)));

    const accurate = accuracy === null || accuracy <= MAX_ACCURACY_M;
    if (accurate) {
      if (trip.lastGood) {
        const [lat0, lon0, t0] = trip.lastGood;
        const step = distanceM(lat0, lon0, latitude, longitude);
        // Katkoksen yli ei lasketa matkaa, kuten palvelimellakaan.
        if (t - t0 <= GAP_SECONDS && step <= GAP_DISTANCE_M) trip.distanceM += step;
      }
      trip.lastGood = [latitude, longitude, t];
      addTrackPoint(trip.track, latitude, longitude);
      const key = tileKey(latitude, longitude, this.smallZoom);
      if (!trip.localTiles.includes(key)) trip.localTiles.push(key);
    }
    this.persist();
    if (trip.unsent.length >= 20) this.sync();
  }

  changeMode(mode) {
    const trip = this.active;
    if (!trip || trip.mode === mode) return;
    trip.mode = mode;
    trip.modeQueue.push({ start_t: this.now(), mode });
    // Kulkutavan vaihto katkaisee paikallisen matkalaskennan, jotta
    // siirtymää ei lasketa lihasvoimamatkaksi.
    trip.lastGood = null;
    this.persist();
    this.sync();
  }

  /** Tauko: sijaintien tallennus keskeytyy, ja kesto pysähtyy. */
  pause() {
    const trip = this.active;
    if (!trip || trip.pausedAt) return;
    trip.pausedAt = this.now();
    trip.pauseQueue = trip.pauseQueue ?? [];
    trip.pauseQueue.push({ start_t: trip.pausedAt, end_t: null });
    this.persist();
    this.sync();
  }

  resume() {
    const trip = this.active;
    if (!trip || !trip.pausedAt) return;
    const end = this.now();
    trip.pauseQueue.push({ start_t: trip.pausedAt, end_t: end });
    trip.pausedTotal = (trip.pausedTotal ?? 0) + (end - trip.pausedAt);
    trip.pausedAt = null;
    trip.lastGood = null; // tauon aikaista siirtymää ei lasketa matkaksi
    this.persist();
    this.sync();
  }

  finish() {
    const trip = this.active;
    if (!trip) return null;
    if (trip.pausedAt) this.resume(); // retki päättyy: myös tauko päättyy
    trip.finishedAt = Math.max(this.now(), trip.lastT);
    this.outbox.push(trip);
    this.active = null;
    this.persist();
    this.sync();
    return trip.id;
  }

  /**
   * Hylkää käynnissä olevan retken: sitä ei tallenneta. Käyttöliittymä kysyy varmistuksen ENNEN tätä kutsua.
   *
   * Retki voi jo olla palvelimella (luotu, pisteitä lähetetty ja ruutuja myönnetty), joten pelkkä paikallinen
   * unohtaminen ei riitä: palvelimelta poistetaan retki, jolloin sen ruudut ja pisteet kumoutuvat. Poisto jonotetaan
   * pysyvästi (`discards`), joten se onnistuu myös, jos yhteys puuttuu nyt tai sovellus suljetaan ennen sitä.
   * Poistoa yritetään aina, koska keskeneräinen lähetys on voinut luoda retken palvelimelle juuri ennen hylkäystä;
   * "retkeä ei löydy" (404) tarkoittaa, että mitään ei ollut poistettavaa.
   */
  discard() {
    const trip = this.active;
    if (!trip) return null;
    trip.discarded = true; // mahdollinen käynnissä oleva lähetys lopettaa tämän retken käsittelyn
    this.active = null;
    this.discards.push({ id: trip.id, player: trip.player ?? null });
    if (this.journal) {
      // Odotetaan vain jo käynnissä olevat varastokirjoitukset (uusia ei enää tule); itseään ei saa odottaa.
      const earlier = [...this.pendingWrites];
      this._track(Promise.all(earlier).then(() => this.journal.dropTrip(trip.id)));
    }
    this.persist();
    this.sync();
    return trip.id;
  }

  /** Poistaa hylätyt retket palvelimelta. Palauttaa false, jos yhteys puuttuu (jatketaan myöhemmin). */
  async _processDiscards() {
    for (const item of [...this.discards]) {
      if (this.api.canActAs && !this.api.canActAs(item.player)) continue;
      const api = item.player && this.api.as ? this.api.as(item.player) : this.api;
      try {
        await api.deleteTrip(item.id);
      } catch (error) {
        if (error.status !== 404) {
          this.lastError = error.message;
          // Yhteys- ja palvelinvirheet (ei vastausta tai 5xx) yritetään uudelleen; muu 4xx ei korjaannu toistamalla.
          if (!error.status || error.status >= 500) return false;
        }
      }
      this.discards = this.discards.filter((d) => d !== item);
      this.persist();
    }
    return true;
  }

  // --- Lähetys ---------------------------------------------------------------

  /**
   * Lähettää kaiken jonossa olevan. Jos lähetys on jo käynnissä, pyyntö
   * merkitään ja lähetys ajetaan heti uudelleen nykyisen päätyttyä. Näin
   * kesken lähetyksen tullut uusi tila (piste, kulkutavan vaihto, yhteyden
   * palautuminen) ei jää odottamaan seuraavaa sattumanvaraista herätettä.
   */
  sync() {
    if (this.syncing) {
      this.syncAgain = true;
      return this.syncing;
    }
    this.syncing = (async () => {
      do {
        this.syncAgain = false;
        await this._syncAll();
      } while (this.syncAgain);
    })().finally(() => {
      this.syncing = null;
      this.onChange(this.snapshot());
    });
    return this.syncing;
  }

  async _syncAll() {
    if (!(await this._processDiscards())) return;
    const trips = [this.active, ...this.outbox].filter(Boolean);
    for (const trip of trips) {
      if (this.api.canActAs && !this.api.canActAs(trip.player)) continue;
      try {
        await this._syncTrip(trip);
        this.lastError = null;
      } catch (error) {
        this.lastError = error.message;
        // Yhteysvirhe koskee kaikkia retkiä: ei yritetä seuraavaa turhaan.
        if (!error.status) return;
      }
    }
    this.persist();
  }

  async _syncTrip(trip) {
    // Retki lähetetään sen pelaajan tietoihin, joka sen aloitti.
    const api = trip.player && this.api.as ? this.api.as(trip.player) : this.api;
    if (trip.discarded) return;
    if (!trip.created) {
      const created = await api.createTrip({ id: trip.id, started_at: trip.startedAt, mode: trip.initialMode, tz: trip.tz, companions: trip.companions ?? [] });
      trip.created = true;
      // Retken luonti voi avata merkin (esim. "Ensimmäinen retki"); sen pisteet ilmoitetaan heti.
      this._receive(trip, created?.changes);
      this.persist();
    }
    while (trip.modeQueue.length && !trip.discarded) {
      const change = trip.modeQueue[0];
      const result = await api.changeMode(trip.id, change.start_t, change.mode);
      trip.modeQueue.shift();
      this._receive(trip, result.changes);
      this.persist();
    }
    // Tauot ennen pisteitä: palvelin tietää heti, ettei tauon yli ole katkos.
    while (trip.pauseQueue?.length && !trip.discarded) {
      const pause = trip.pauseQueue[0];
      const result = await api.pause(trip.id, pause.start_t, pause.end_t);
      trip.pauseQueue.shift();
      this._receive(trip, result.changes);
      this.persist();
    }
    while (trip.unsent.length && !trip.discarded) {
      // Pisteitä voi tulla lisää lähetyksen aikana; ne lisätään jonon
      // loppuun, joten lähetetty erä poistetaan alusta sen pituuden verran.
      const batch = trip.unsent.slice(0, BATCH_SIZE);
      const result = await api.addPoints(trip.id, batch);
      trip.unsent.splice(0, batch.length);
      this.lastStats = result.stats ?? this.lastStats;
      this._receive(trip, result.changes);
      this.persist();
      // Palvelin on kuitannut erän: nyt pisteet saa poistaa varastosta. Epäonnistunut
      // poisto ei haittaa; pisteet lähetetään myöhemmin uudelleen ja palvelin ohittaa ne.
      if (this.journal && batch.length) this._track(this.journal.ackThrough(trip.id, batch[batch.length - 1].seq));
    }
    if (!trip.discarded && trip.finishedAt && !trip.unsent.length && !trip.modeQueue.length && !trip.pauseQueue?.length) {
      const finished = await api.finishTrip(trip.id, trip.finishedAt);
      this.outbox = this.outbox.filter((t) => t.id !== trip.id);
      this.persist();
      if (this.journal) this._track(this.journal.dropTrip(trip.id));
      if (finished?.companions_earned && Object.keys(finished.companions_earned).length) {
        this.onCompanions(finished.companions_earned, trip);
      }
    }
  }

  _receive(trip, changes = []) {
    if (!changes.length) return;
    for (const c of changes) {
      trip.earned += c.delta;
      if (c.kind === "tile") trip.newTiles += Math.sign(c.delta);
    }
    this.onAwards(changes, trip.id);
  }
}
